import { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Button,
  Flex,
  SearchInput,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useSearchParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import Dropdown from '@Components/Common/Dropdown';
import CorrelatedExpressionsTable from './table';
import { CorrelatedExpression, RuleTypeFilter } from '../types';
import { AddIcon } from '@tonic-one/react-icons';
import { useExpressionMutations, useRiskAIExpressionsQuery } from '../query';
import CustomExpressionModal from './CustomExpressionModal';
import isEqual from 'lodash/isEqual';
import { useSocialGraphEnabledQuery } from '../../SocialGraph/query';
import { filterSocialGraphExpressions } from '../utils';

const CorrelatedExpressions = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [isUserDefinedModalOpen, setIsUserDefinedModalOpen] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [expressionTypeFilter, setExpressionTypeFilter] =
    useState<RuleTypeFilter>(RuleTypeFilter.All);
  const [searchParams, setSearchParams] = useSearchParams();
  const { createExpMutation, deleteExpMutation, editExpMutation } =
    useExpressionMutations();
  const { data: _expressions, isLoading: isLoadingExpressions } =
    useRiskAIExpressionsQuery();
  const { data: socialGraphEnabled, isLoading: isLoadingSocialGraph } =
    useSocialGraphEnabledQuery();

  const expressions = useMemo(() => {
    if (!_expressions) return undefined;
    return filterSocialGraphExpressions(
      _expressions ?? [],
      socialGraphEnabled ?? false
    );
  }, [_expressions, socialGraphEnabled]);

  const isLoading = isLoadingExpressions || isLoadingSocialGraph;

  // Handle highlight query parameter to automatically search for and highlight a specific signal
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (highlight && expressions) {
      // Set both the search input value and the name filter to search for the highlighted signal
      setSearchInputValue(highlight);
      setNameFilter(highlight);

      // Clear the highlight parameter from the URL to clean up the URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('highlight');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, expressions]);

  const [searchInputValue, setSearchInputValue] = useState('');

  const debounceSetSearch = debounce((value: string) => {
    setNameFilter(value);
  }, 200);

  const isDuplicatedName = useCallback(
    (newExpression: CorrelatedExpression) => {
      return (
        (expressions ?? []).filter(
          (r) =>
            r.raiExpId !== newExpression.raiExpId &&
            r.raiExpName?.trim() === newExpression.raiExpName.trim()
        ).length > 0
      );
    },
    [expressions]
  );

  const handleCreateExpression = useCallback(
    async (expression: CorrelatedExpression) => {
      if (isDuplicatedName(expression)) {
        window.showNotification({
          severity: 'error',
          titleMsg: t('globalSetting.correlatedExpressions.expressionExists'),
        });
        throw new Error(
          t('globalSetting.correlatedExpressions.expressionExists')
        ); // to prevent closing the modal
      }
      await createExpMutation.mutateAsync(expression);
    },
    [createExpMutation, isDuplicatedName, t]
  );

  const expressionHasChanged = useCallback(
    (expression: CorrelatedExpression) => {
      const originalExpression = expressions?.find(
        (r) => r.raiExpId === expression.raiExpId
      );
      return !isEqual(originalExpression, expression);
    },
    [expressions]
  );

  const handleEditExpression = useCallback(
    async (expression: CorrelatedExpression) => {
      if (expressionHasChanged(expression)) {
        if (isDuplicatedName(expression)) {
          window.showNotification({
            severity: 'error',
            titleMsg: t('globalSetting.correlatedExpressions.expressionExists'),
          });
          throw new Error(
            t('globalSetting.correlatedExpressions.expressionExists')
          ); // to prevent closing the modal
        }
        await editExpMutation.mutateAsync(expression);
      }
    },
    [editExpMutation, isDuplicatedName, expressionHasChanged, t]
  );

  return (
    <Flex flexDirection="column" flex="auto">
      <Stack gap="1x" mb="4x">
        <Text color={colorStyle.color.secondary}>
          {t('globalSetting.correlatedExpressions.description')}
        </Text>
        <Flex columnGap="1x">
          <Text>
            {t('globalSetting.correlatedRules.description.noteLabel')}:
          </Text>
          <Text color={colorStyle.color.secondary}>
            {t('globalSetting.correlatedRules.description.note')}
          </Text>
        </Flex>
      </Stack>
      <Stack gap="2x" flex="auto" mb="4x">
        <Stack flexDirection="row" gap="2x">
          <Button
            variant="primary"
            gap="1x"
            width="max-content"
            onClick={() => setIsUserDefinedModalOpen(true)}
          >
            <AddIcon />
            {t('globalSetting.correlatedExpressions.addExpression')}
          </Button>
          <Dropdown
            placeholder={t('globalSetting.correlatedExpressions.filterType')}
            minWidth={188}
            maxWidth={225}
            value={expressionTypeFilter}
            options={[
              RuleTypeFilter.All,
              RuleTypeFilter.UserDefined,
              RuleTypeFilter.PreDefined,
            ]}
            renderOption={(option) =>
              option === RuleTypeFilter.All
                ? t('common.label.all')
                : option === RuleTypeFilter.UserDefined
                  ? t('globalSetting.correlatedRules.userDefined')
                  : t('globalSetting.correlatedRules.preDefined')
            }
            onChange={(value: RuleTypeFilter) => setExpressionTypeFilter(value)}
          />
          <SearchInput
            width="325px"
            value={searchInputValue}
            placeholder={t(
              'globalSetting.correlatedExpressions.searchFilterName'
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              setSearchInputValue(value);
              debounceSetSearch(value);
            }}
            onClearInput={() => {
              setSearchInputValue('');
              setNameFilter('');
            }}
          />
        </Stack>
        <CorrelatedExpressionsTable
          expressions={expressions ?? []}
          nameFilter={nameFilter}
          expressionTypeFilter={expressionTypeFilter}
          loading={isLoading}
          onCopyExpression={handleCreateExpression}
          onEditExpression={handleEditExpression}
          onDeleteExpression={async (expressionId) => {
            await deleteExpMutation.mutateAsync(expressionId);
          }}
        />
        {isUserDefinedModalOpen && (
          <CustomExpressionModal
            isOpen={isUserDefinedModalOpen}
            onSave={async (expression) => {
              try {
                await handleCreateExpression(expression);
                setIsUserDefinedModalOpen(false);
              } catch (e) {
                // User has to modify the expression name or retry
                console.error(e);
              }
            }}
            onClose={() => setIsUserDefinedModalOpen(false)}
          />
        )}
      </Stack>
    </Flex>
  );
};

export default CorrelatedExpressions;
